import { Form, message } from 'antd';
import { titleRef } from 'features/booking/BookingOnlineV2/HeaderStep';
import { IAppointmentSuccess } from 'features/booking/BookingOnlineV2/SuccessStep';
import appointmentApis from 'features/booking/services/apis';
import { IBodyApiBookingMultiple, IBodyApiBookingMultipleItem } from 'features/booking/services/types/api';
import { IAppointmentItemData } from 'features/booking/services/types/apt';
import { debounce, first, get, last } from 'lodash';
import moment, { Moment } from 'moment';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IResponseDataBody } from 'services/response';
import shopSelectors from 'services/shop/selectors';
import { ICategoryItemData } from 'services/shop/types/categories';
import { IStaffItemData } from 'services/shop/types/staff';
import styled from 'styled-components';
import { formatPhone } from 'utils/formatPhone';
import { trackGoogleConversion } from 'utils/trackGoogleConversion';
import uuid from 'utils/uuid';
import CustomerBottomSheet, { customerBs, ICustomerEditorFormData } from '../BookingOnlineV2/CustomerBottomSheet';
import { customerServicesStepRef } from '../BookingOnlineV2/CustomerServicesStep';
import { dateStaffStepRef } from '../BookingOnlineV2/DateStaffStep';
import BookingV2Loading, { bookingV2LoadingRef } from '../BookingOnlineV2/Loading';
import ModalConfirmOvertime, { useModalConfirmOvertimeRef } from '../BookingOnlineV2/ModalConfirmOvertime';
import NotificationModal from '../BookingOnlineV2/NotificationModal';
import { timeSlotStepRef } from '../BookingOnlineV2/TimeSlotStep';
import { IFormValueServiceItem, IGroupCustomerItem } from '../BookingOnlineV2/types';
import StepNavigation, { STEP_NAVIGATION } from './StepNavigation';
/**
 *  Chình lại flow booking web customer.
    call api available-time-slot-store → lấy danh sách timeslot rảnh của tiệm.
    call api shop-off-day lấy những ngày nghỉ của tiệm trong 1 khoản thời gian, có thể theo cái calender hiển thị.
    Call api /list-staff-by-time để lấy danh sách staff làm theo service và giờ trống đó
    Call api booking-multiple--> booking cuối cùng
    
 * @returns 
 */

const initialValues: BookingOnlineV4FormValues = {
  customerEditor: null,
  customerId: '',
  customerPhone: '',
  customerName: '',
  previousStep: null,
  currentStep: 'date_staff',
  date: moment(),
  staff: null,
  dayOffs: [],
  startTime: '',
  CUSTOMER_VALID: false,
  DATE_STAFF_VALID: false,
  TIME_SLOT_VALID: false,
  SERVICE_VALID: false,
  request: true,
  services: [],
  staffList: [],
  note: '',
  appointment: null,
  categories: [],
  acceptPolicyDeposit: false,
  customers: [
    {
      name: '',
      uuid: uuid(),
      requestStaff: true,
      services: []
    },
  ],
  timeslots: [],
};

export type BookingOnlineV4FormValues = {
  customerPhone: string;
  customerName: string;
  customerId: string;
  previousStep: STEP_NAVIGATION | null;
  currentStep: STEP_NAVIGATION;
  date: Moment;
  staff: IStaffItemData | null;
  startTime: string;
  CUSTOMER_VALID: boolean;
  DATE_STAFF_VALID: boolean;
  TIME_SLOT_VALID: boolean;
  SERVICE_VALID: boolean;
  services: IFormValueServiceItem[];
  staffList: IStaffItemData[];
  request: boolean;
  dayOffs: Moment[];
  note: string;
  appointment: IAppointmentSuccess | null;
  categories: ICategoryItemData[];
  acceptPolicyDeposit: boolean;
  customerEditor: ICustomerEditorFormData | null;
  customers: IGroupCustomerItem[];
  timeslots: string[];
};

const BookingOnlineV4UI = () => {
  const modalConfirm = useModalConfirmOvertimeRef();
  const [form] = Form.useForm();
  const allSetting = shopSelectors.data.allShopSetting();
  const salonInfo = shopSelectors.getShopInfo();
  const [searchParams] = useSearchParams();
  const rwg_token = searchParams.get('rwg_token');
  const existing = document.cookie.includes('_rwgToken=');
  const initialServiceId = searchParams.get('serviceId');
  const allServices = shopSelectors.data.allServices();
  if (rwg_token && !existing) {
    document.cookie = `_rwgToken=${encodeURIComponent(rwg_token)}; max-age=2592000; path=/`;
  }

  const onNextFrom = (currentStep: STEP_NAVIGATION) => {
    switch (currentStep) {
      case 'date_staff': {
        form.setFieldValue('previousStep', currentStep || 'date_staff');
        form.setFieldValue('currentStep', 'time_slot');
        break;
      }
      case 'time_slot': {
        form.setFieldValue('previousStep', currentStep || 'time_slot');
        form.setFieldValue('currentStep', 'service');
        break;
      }
      case 'service': {
        form.setFieldValue('previousStep', currentStep || 'service');
        form.setFieldValue('currentStep', 'customer');
        break;
      }
      case 'customer': {
        form.setFieldValue('previousStep', currentStep || 'customer');
        form.setFieldValue('currentStep', 'review');
        break;
      }
      default:
        break;
    }

    setTimeout(() => {
      titleRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }, 10);
  };

  const validateOvertime = async (data: BookingOnlineV4FormValues, customers: IGroupCustomerItem[]) => {
    const endTime = last(last(customers)?.services)?.endTime;
    const latestTimeSlot = last(data.timeslots);
    if (endTime && latestTimeSlot) {
      const endTimeMoment = moment(endTime, 'hh:mm A');
      const lastedTimeMoment = moment(endTimeMoment.format('MM/DD/YYYY') + ' ' + latestTimeSlot, 'MM/DD/YYYY hh:mm A');
      if (endTimeMoment > lastedTimeMoment) {
        modalConfirm.current?.open(`The selected time falls outside our service hours. Please call in ${formatPhone(salonInfo?.phone || '')} to schedule your appointment!`);
        return false;
      }
    }
    return true;
  };

  const handleInitialData = useCallback(debounce((date: string, customerId: string) => {
    dateStaffStepRef.current?.fetch(date, customerId);
  }, 200), []);

  useEffect(() => {
    handleInitialData(initialValues.date.format('MM-DD-YYYY'), initialValues.customerId);
  }, []);

  const onDateTimeSubmit = (data: BookingOnlineV4FormValues) => {
    form.setFieldValue('DATE_STAFF_VALID', true);
    setTimeout(() => {
      timeSlotStepRef.current?.fetch(data.date.format('MM-DD-YYYY'), data.staff?.id || '');
    }, 50);
    onNextFrom('date_staff');
  };

  const onTimeSlotSubmit = (data: BookingOnlineV4FormValues) => {
    form.setFieldValue('TIME_SLOT_VALID', true);
    setTimeout(() => {
      customerServicesStepRef.current?.fetch(data.staff?.id || '');
    }, 50);
    const initialService = allServices.find((o) => o.id?.toString() === initialServiceId?.toString());
    if (initialService) {
      const customers: IGroupCustomerItem[] = data.customers.map((c) => ({
        ...c, services: [{
          id: initialService.id,
          catId: initialService.catId,
          duration: initialService.duration,
          serviceName: initialService.serviceName,
          staffName: '',
          staffId: '',
          local_uuid: uuid(),
          staffAvatar: '',
          startTime: '',
          endTime: '',
          price: initialService.priceSell,
        }]
      }));
      form.setFieldValue('customers', customers);
      onServiceSubmit({ ...data, customers });
      return;
    } else {
      onNextFrom('time_slot');
    }
  };

  const onServiceSubmit = async (data: BookingOnlineV4FormValues) => {
    form.setFieldValue('SERVICE_VALID', true);
    const customers = data.customers.map((c) => {
      let startTime = data.startTime;
      const services = c.services.map((o) => {
        const endTime = moment(startTime, 'hh:mm A').add(o.duration, 'minutes').format('hh:mm A');
        const result = {
          ...o,
          startTime,
          endTime,
        };
        if (data.customers.length === 1) {
          result.staffId = data.staff?.id || '';
          result.staffName = data.staff?.nickName || '';
          result.staffAvatar = data.staff?.urlImage || '';
        }

        startTime = endTime;
        return result;
      });
      return { ...c, services };
    });
    const valid = await validateOvertime(data, customers);
    if (!valid) {
      form.setFieldValue('SERVICE_VALID', false);
      return;
    }
    form.setFieldValue('customers', customers);
    onNextFrom('service');
    return;
  };

  const onCustomerSubmit = async (data: BookingOnlineV4FormValues) => {
    form.setFieldValue('customers', data.customers.map((c) => ({ ...c, name: data.customerName })) as IGroupCustomerItem[]);
    form.setFieldValue('CUSTOMER_VALID', true);
    onNextFrom('customer');
    return;
  };

  const handleSubmit = async (values: any) => {
    const data = values as BookingOnlineV4FormValues;
    if (!data.DATE_STAFF_VALID) {
      onDateTimeSubmit(data);
      return;
    } else if (data.currentStep === 'date_staff') {
      onNextFrom('date_staff');
      return;
    }

    if (!data.TIME_SLOT_VALID) {
      onTimeSlotSubmit(data);
      return;
    } else if (data.currentStep === 'time_slot') {
      onNextFrom('time_slot');
      return;
    }

    if (!data.SERVICE_VALID) {
      onServiceSubmit(data);
      return;
    } else if (data.currentStep === 'service') {
      onNextFrom('service');
      return;
    }

    if (!data.CUSTOMER_VALID) {
      onCustomerSubmit(data);
      return;
    } else if (data.currentStep === 'customer') {
      onNextFrom('customer');
      return;
    }

    if (data.currentStep !== 'review') return;

    const items: IBodyApiBookingMultiple['items'] = [];
    data.customers.map((c) =>
      c.services.map((o) => {
        const startTime = `${data.date.format('MM/DD/YYYY')} ${moment(o.startTime, 'hh:mm A').format('HH:mm')}`;
        const endTime = `${data.date.format('MM/DD/YYYY')} ${moment(o.endTime, 'hh:mm A').format('HH:mm')}`;

        const result: IBodyApiBookingMultipleItem = {
          staffId: o.staffId,
          serviceId: o.id,
          requestStaff: !!c.requestStaff,
          startTime,
          endTime,
        };
        if (data.customers.length > 1) {
          result.customerName = c.name || '';
          result.tempCustomerId = c.uuid || '';
        }
        items.push(result);
      })
    );

    const isEmptyStaff = !items.every((o) => !!o.staffId);
    if (isEmptyStaff) return message.warning('Please select the staff');

    const body: IBodyApiBookingMultiple = {
      createdBy: '',
      customerId: data.customerId || '',
      bookingType: 'ONLINE',
      customerName: data.customerName || '',
      customerPhone: (data.customerPhone || '').replace(/[^0-9]/g, ''),
      note: data.note || '',
      repeat: 0,
      items,
      numberCustomers: data.customers.length,
      rwgToken: rwg_token || undefined,
    };

    bookingV2LoadingRef.current?.open();
    try {
      const res: IResponseDataBody<IAppointmentItemData[]> = await appointmentApis.bookingMultiple(body);
      const successData = first(res.data.data);
      if (successData) {
        form.setFieldValue('currentStep', 'success');
        const startTime = moment(successData.startTime, 'MM-DD-YYYY HH:mm:ss');
        const endTime = moment(successData.endTime, 'MM-DD-YYYY HH:mm:ss');
        form.setFieldValue('appointment', {
          qrCode: successData.qrCode || '',
          customerName: successData.customerName,
          customerPhone: successData.customerPhone,
          date: startTime.format('MM-DD-YYYY'),
          items: res.data.data,
          startTime: startTime.format('hh:mm A'),
          endTime: endTime.format('hh:mm A'),
          note: data.note,
        } as IAppointmentSuccess);
      }

      trackGoogleConversion();
    } catch (error: any) {
      message.error(error.response.data.message);
    } finally {
      bookingV2LoadingRef.current?.close();
    }
  };

  return (
    <FormStyled
      form={form}
      onFinish={handleSubmit}
      initialValues={initialValues}
      onFinishFailed={(errorInfo: any) => {
        const error: string = get(first(errorInfo.errorFields), ['errors', 0], '') || '';
        if (!error) return;
        message.warning(error);
      }}
    >
      <Form.Item name={'customerId'} noStyle />
      <Form.Item name={'customerPhone'} noStyle />
      <Form.Item name={'customerName'} noStyle />
      <Form.Item name={'currentStep'} noStyle />
      <Form.Item name={'date'} noStyle />
      <Form.Item name={'startTime'} noStyle />
      <Form.Item name={'services'} noStyle />
      <Form.Item name={'timeslots'} noStyle />
      <Form.Item name={'request'} noStyle />
      <Form.Item name={'staffList'} noStyle />
      <Form.Item name={'staff'} noStyle />
      <Form.Item name={'dayOffs'} noStyle />
      <Form.Item name={'CUSTOMER_VALID'} noStyle />
      <Form.Item name={'DATE_STAFF_VALID'} noStyle />
      <Form.Item name={'TIME_SLOT_VALID'} noStyle />
      <Form.Item name={'SERVICE_VALID'} noStyle />
      <Form.Item name={'categories'} noStyle />
      <Form.Item name={'appointment'} noStyle />
      <Form.Item name={'acceptPolicyDeposit'} noStyle />
      <Form.Item name={'customers'} noStyle />
      <Form.Item noStyle name={'currentStep'}>
        <StepNavigation />
      </Form.Item>
      <BookingV2Loading ref={bookingV2LoadingRef} />
      <CustomerBottomSheet ref={customerBs} />
      <ModalConfirmOvertime ref={modalConfirm} />
    </FormStyled>
  );
};

const BookingOnlineV4 = () => {
  useEffect(() => {
    document.body.style.background = '#232F3E';
  }, []);
  return (
    <Wrapper>
      <div className='box-container-responsive'>
        <BookingOnlineV4UI />
      </div>
      <NotificationModal />
    </Wrapper>
  );
};
const Wrapper = styled.div`
  min-height: 100vh;
  background: #232f3e;
  .box-container-responsive {
    align-self: stretch;
    background: #f5f5f5;
    height: 100%;
    position: relative;
    max-width: 476px;
    margin: 0 auto;
    box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.15);

    &::-webkit-scrollbar {
      width: 0px;
    }
  }

  @media only screen and (max-width: 500px) {
    .box-container-responsive {
      width: 100%;
      max-width: unset;
    }
  }
`;

export default BookingOnlineV4;


const FormStyled = styled(Form)`
  background: #f5f5f5;
  height: 100%;
  display: flex;
  flex-direction: column;

  .ant-form-item {
    align-self: stretch;
    .ant-form-item-row {
      position: relative;
      padding-top: 12px;
    }

    .ant-form-item-label {
      padding: 0;
      z-index: 2;
      position: absolute;
      top: 0px;
      left: 8px;
      label {
        height: unset;
        z-index: 9;
        background: #fff;
        &::before {
          display: none !important;
        }

        padding: 0 2px;

        color: #1d2129;
        font-family: Poppins;
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 6px;
      }
    }
    .ant-form-item-control {
      z-index: 1;
    }

    &:focus-within {
      .ant-form-item-label label {
        color: #2463eb;
      }
    }

    .ant-form-item-control-input input {
      font-size: 18px;
      text-align: center;
      height: 56px;

      color: #1d1b20;
      &::placeholder {
        color: #64748b;
      }

      &:-ms-input-placeholder {
        color: #64748b;
      }

      &::-ms-input-placeholder {
        color: #64748b;
      }
    }
  }
`;
